import { useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import TrackingAPI from 'api/TrackingAPI';
import hero_img from 'assets/BG_full.webp';
import logo from 'assets/eplay_logo.svg';
import mail_icon from 'assets/mail.svg';
import EmailSendedIcon from 'assets/sended_message_icon.svg?react';
import NiceModal from 'components/modals/NiceModal';
import { ErrorMessage, Formik } from 'formik';
import CommonHelper from 'helpers/CommonHelper';
import styled from 'styled-components';
import { isBlank } from 'utils/storeUtils';
import useQueryParams from 'utils/useQueryParams';
import * as Yup from 'yup';

import Actions from './components/Actions/Actions';
import SecurityInfo from './components/SecurityInfo';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

const CpaCloseButton = styled.div`
  @media (max-width: 430px) {
    position: fixed;
    right: 24px;
    top: 24px;
    z-index: 1000;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const CpaPage = () => {
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);
  const { queryParams } = useQueryParams();

  const verifyEmail = queryParams?.verifyEmail ?? '';
  const isSendedEmail = !isBlank(verifyEmail);

  useEffect(() => {
    const trackEvent = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;
      try {
        TrackingAPI.trackEvent('cpa_modal_opened');
      } catch (error) {
        throw error;
      }
    };

    trackEvent();
  }, []);

  return (
    <PageWrapper>
      <PageHeader>
        <Logo src={logo} alt="EroPlay.ai" />
      </PageHeader>
      <NiceModal
        withBlurBackground={false}
        onClose={() => navigate('/')}
        closeOnEsc={false}
        customCloseButton={isMobile ? <CpaCloseButton /> : null}
      >
        <Formik
          initialValues={{ email: '' }}
          validationSchema={validationSchema}
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={() => { }}
        >
          {({ values, setFieldValue, setFieldError, errors, validateForm, setFieldTouched }) => {
            const handleFieldChange = (field: string, value: string) => {
              setFieldValue(field, value);
              setFieldError(field, '');
            };

            return (
              <FormContainer isSendedEmail={isSendedEmail}>
                <MobileLogo>
                  <Logo src={logo} alt="EroPlay.ai" />
                </MobileLogo>
                {!isSendedEmail && (
                  <>
                    <Title>Create Account</Title>
                    <FormField>
                      <TextField
                        className="focus-within:ring-0 focus:ring-0"
                        icon={mail_icon}
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={values.email}
                        placeholder='E-mail'
                        onChange={e => handleFieldChange('email', e.target.value)}
                      />
                      {errors.email && <ErrorMessage name="email" component={StyledErrorMessage} />}
                      <EmailDescription>We&apos;ll send verification link to this email</EmailDescription>
                    </FormField>
                    <Actions
                      values={values}
                      validateForm={validateForm}
                      setFieldTouched={setFieldTouched}
                    />
                    <SecurityInfo />
                  </>
                )}
                {isSendedEmail && (
                  <SendedEmailContainer>
                    <EmailSendedIconContainer>
                      <EmailSendedIcon />
                    </EmailSendedIconContainer>
                    <Title>We sent an mail<br />to {`${verifyEmail}`}</Title>
                    <SendedEmailSubtitle>Inside, you&apos;ll find the password for your new account. Enjoy the stories!</SendedEmailSubtitle>
                  </SendedEmailContainer>
                )}
              </FormContainer>
            );
          }}
        </Formik>
      </NiceModal>
    </PageWrapper>
  );
};

export default CpaPage;

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: rgba(16, 13, 11, 1);
  background-size: cover;
  background-position: center;
  background-image: url(${() => CommonHelper.getEroplayCdnUrl(hero_img)});
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 430px) {
    background: rgba(29, 29, 29, 1);
    padding: 0;
    margin: 0;
  }
`;

const EmailDescription = styled.div`
  font-size: 12px;
  margin-top: 0.5rem;
  padding-left: 6px;
  color: #A9A9A9;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const TextField = styled.input<{ icon: string }>`
  background-color: rgba(19, 19, 19, 1) !important;
  border: none;
  border-radius: 12px;
  padding: 14px;
  color: white;
  outline: none;
  font-size: 16px;

  background: url(${props => props?.icon}) no-repeat;
  background-size: 18px;
  background-position-x: 16px;
  background-position-y: center;
  padding-left: 46px;

  :focus {
    outline: 1px solid rgb(255, 126, 85);
    outline-offset: 0px;
  }

  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }
`;

const PageHeader = styled.div`
  width: 100%;
  padding: 24px;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;

  @media (max-width: 430px) {
    display: none;
  }
`;

const Logo = styled.img`
  height: 32px;
  width: auto;

  @media (max-width: 375px) {
    height: 24px;
  }
`;

const FormContainer = styled.div<{ isSendedEmail: boolean }>`
  width: 100%;
  max-width: 400px;
  background: rgba(29, 29, 29, 1);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: ${props => props.isSendedEmail ? '470px' : 'auto'};

  @media (max-width: 430px) {
    max-width: 100%;
    min-height: 100vh;
    height: 100vh;
    border-radius: 0;
    margin: 0;
    padding: 0 24px;
    box-shadow: none;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
  }

  @media (max-width: 375px) {
    padding: 0 16px;
    gap: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin: 0;
  text-align: center;

  @media (max-width: 375px) {
    font-size: 20px;
  }
`;

const SendedEmailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  ${Title} {
    margin-bottom: 16px;
    line-height: 22px;
  }

  @media (max-width: 430px) {
    justify-content: flex-start;
  }
`;

const SendedEmailSubtitle = styled.div`
  font-size: 16px;
  line-height: 18px;
  color: rgba(169, 169, 169, 0.8);
  text-align: center;
  max-width: 320px;
  color: #FFFFFF;
`;

const EmailSendedIconContainer = styled.div`
  background-color: #282828;
  border-radius: 100%;
  padding: 16px 12px 12px 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledErrorMessage = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 4px;
  padding-left: 6px;
`;

const MobileLogo = styled.div`
  display: none;
  justify-content: center;

  @media (max-width: 430px) {
    display: flex;
    padding-top: 48px;
    margin-bottom: 32px;
  }

  @media (max-width: 375px) {
    padding-top: 32px;
    margin-bottom: 24px;
  }
`;

